import React from "react";
import { StepShell } from "@/components/StepShell";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AMSLER_ESITI, AMSLER_ANOMALIE } from "@/lib/screeningData";
import { useI18n } from "@/i18n";
import { Grid3x3 } from "lucide-react";

// La griglia si disegna alla misura nominale e si rimpicciolisce con il contenitore
// (`maxWidth: 100%`): il viewBox mantiene le proporzioni, quindi su schermi stretti la griglia
// resta quadrata e completa invece di essere tagliata.
const AmslerGrid = ({ size = 480 }) => {
  const cells = 17;
  const step = size / cells;
  const lines = [];
  for (let i = 0; i <= cells; i++) {
    const p = i * step;
    lines.push(<line key={`h${i}`} x1={0} y1={p} x2={size} y2={p} stroke="#000" strokeWidth={1} />);
    lines.push(<line key={`v${i}`} x1={p} y1={0} x2={p} y2={size} stroke="#000" strokeWidth={1} />);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ background: "#fff", maxWidth: "100%", height: "auto" }} data-testid="amsler-grid">
      {lines}
      <circle cx={size / 2} cy={size / 2} r={5} fill="#000" />
    </svg>
  );
};

export const AmslerTest = ({ data, update }) => {
  const { t, tc } = useI18n();
  const a = data.amsler;
  const setEye = (eye, patch) => update("amsler", { [eye]: { ...a[eye], ...patch } });

  const EyePanel = ({ eye, label, state, onEsito, onToggle }) => (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <p className="text-sm font-semibold text-slate-800 mb-3">{label}</p>
      <RadioGroup className="space-y-2" value={state.esito} onValueChange={onEsito}>
        {AMSLER_ESITI.map((e) => (
          <label key={e} htmlFor={`amsler-${eye}-${e}`}
            className={`flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
              state.esito === e ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"
            }`}>
            <RadioGroupItem value={e} id={`amsler-${eye}-${e}`} data-testid={`amsler-${eye}-esito-${e.toLowerCase().replace(/\s+/g, "-")}`} />
            <span className="text-sm text-slate-700">{tc(e)}</span>
          </label>
        ))}
      </RadioGroup>
      {state.esito === "Alterata" && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("am_anomalies")}</p>
          {AMSLER_ANOMALIE.map((an) => (
            <label key={an.key} htmlFor={`amsler-${eye}-${an.key}`} className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox id={`amsler-${eye}-${an.key}`} data-testid={`amsler-${eye}-${an.key}`}
                checked={!!state.anomalie[an.key]} onCheckedChange={(v) => onToggle(an.key, !!v)} />
              <span className="text-sm text-slate-700">{t("amsan_" + an.key)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <StepShell icon={Grid3x3} title={t("am_title")} subtitle={t("am_sub")} testId="amsler-step">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="bg-white px-6 py-10 flex justify-center"><AmslerGrid size={480} /></div>
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          <p className="text-sm font-semibold text-slate-800 mb-3">{t("am_result")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EyePanel eye="od" label={t("eye_od")} state={a.od}
              onEsito={(v) => setEye("od", { esito: v })}
              onToggle={(k, val) => setEye("od", { anomalie: { ...a.od.anomalie, [k]: val } })} />
            <EyePanel eye="os" label={t("eye_os")} state={a.os}
              onEsito={(v) => setEye("os", { esito: v })}
              onToggle={(k, val) => setEye("os", { anomalie: { ...a.os.anomalie, [k]: val } })} />
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("notes")}</Label>
            <Textarea data-testid="amsler-note-input" rows={2} value={a.note}
              onChange={(e) => update("amsler", { note: e.target.value })} placeholder={t("notes_ph")} />
          </div>
        </div>
      </div>
    </StepShell>
  );
};
